import { useEffect, useRef } from "react";
import {
  CHOICE_TYPES,
  TRANSLATABLE_TYPES,
  settingOf,
} from "../settings/catalog";
import {
  LOGIN_DISABLED_KEY,
  LOGIN_LINK_KEY,
  LOGIN_MODES,
  LOGIN_OTP_KEY,
  loginModeOf,
} from "../settings/catalog";
import { languageName } from "../settings/nomDeLangue";
import {
  CLOSED_AT_KEY,
  closingPassed,
  fieldFromInstant,
  instantFromField,
  zoneNote,
} from "../funnel/cloture";
import { useSettings } from "../settings/context";
import { EVENT_NAME_KEY, eventNameOf } from "../settings/eventName";
import { useEvent } from "../context/EventProvider";
import type { Setting } from "../settings/types";
import { Help } from "./Aide";
import { Provenance } from "./Provenance";
import { CssRules } from "./CssRules";
import { RecapFields } from "./RecapFields";
import { NewForm } from "./NewForm";
import { FormEditor } from "./FormEditor";
import { HtmlField } from "./HtmlField";
import { ActionEmail } from "./ActionEmail";
import { PagesManager } from "./PagesManager";
import { StepsManager } from "./StepsManager";
import { Upload } from "./Upload";
import { ColorField } from "./ColorField";
import { normalizeHex } from "../theme/color";

/**
 * Les choix d'un sélecteur, groupés par leur `groupe` et dans leur ordre d'arrivée.
 *
 * ⚠️ L'ORDRE DES GROUPES EST CELUI DE LA LISTE, pas un tri alphabétique : le catalogue
 * a déjà rangé ce qui compte devant — les formulaires de l'événement avant ceux du
 * domaine. Retrier ici ferait remonter « Formulaires du domaine » par la seule vertu du
 * D avant le F.
 */
/** Les sélecteurs qui désignent un FORMULAIRE, et sous lesquels on peut en créer un. */
const FORM_TYPES: ReadonlySet<Setting["type"]> = new Set([
  "select-forms-user",
  "select-forms-company",
]);

function grouper(
  options: ReadonlyArray<{ value: string; label: string; group?: string }>,
): Array<[string | undefined, Array<{ value: string; label: string }>]> {
  const out: Array<
    [string | undefined, Array<{ value: string; label: string }>]
  > = [];
  for (const o of options) {
    const last = out[out.length - 1];
    if (last && last[0] === o.group) last[1].push(o);
    else out.push([o.group, [o]]);
  }
  return out;
}

/**
 * ONE SETTING ROW.
 *
 * It reads through the CASCADE and writes at the CURRENT SCOPE. Alongside the
 * control itself it therefore carries the answer to the only question that
 * matters when four sources compete for one value: where does what I see come
 * from, and what am I about to change?
 *
 * Hovering POINTS AT the page element (`data-cfg`) — no guessing which setting
 * drives what.
 */
/**
 * Allume sur la page le bloc qu'un réglage règle, et éteint tous les autres.
 *
 * ⚠️ TOUS les surlignages sont retirés d'abord, plutôt que de basculer le seul bloc de
 * cette ligne. Une ligne peut perdre le pointeur sans jamais recevoir `mouseleave` : il
 * suffit qu'elle soit démontée pendant le survol, ce qui arrive dès la première entrée
 * en mode organisation. Un surlignage resté allumé ment sur ce qu'on modifie. Tout
 * éteindre d'abord en laisse au plus un, et répare l'état de lui-même.
 *
 * Partagé avec la grille de pastilles (`Chips.tsx`), qui pointe le même bloc.
 */
export function pointAt(target: string | undefined): void {
  document
    .querySelectorAll(".aimed")
    .forEach((el) => el.classList.remove("aimed"));
  if (!target) return;
  document
    .querySelectorAll(`[data-cfg="${target}"]`)
    .forEach((el) => el.classList.add("aimed"));
}

/**
 * Amène une ligne désignée par la recherche sous les yeux de l'organisateur.
 *
 * ⚠️ AU CENTRE, PAS EN HAUT : le titre de la famille colle au haut du panneau et aurait
 * recouvert la ligne qu'on vient de chercher.
 */
export function useBringIntoView<T extends HTMLElement>(aimed: boolean) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (aimed) ref.current?.scrollIntoView({ block: "center" });
  }, [aimed]);
  return ref;
}

/**
 * Ce que la pastille d'une couleur non réglée peint : la couleur que le SITE applique.
 *
 * ⚠️ JAMAIS UN NOIR DE REMPLISSAGE. Le site d'un événement neuf a un fond presque blanc ;
 * une pastille noire, et un sélecteur qui s'ouvre sur le noir, font croire à
 * l'organisatrice que son fond est noir. Le défaut vient du catalogue (`fallback`), le
 * même que celui du thème. Une couleur qui, vide, est celle d'un autre réglage
 * (`fallbackFrom`) peint ce réglage tel qu'il se lit maintenant.
 */
function unsetColor(
  setting: Setting,
  value: (key: string) => unknown,
): string {
  if (setting.fallbackFrom) {
    const raw = value(setting.fallbackFrom);
    return (
      normalizeHex(typeof raw === "string" ? raw : "") ??
      settingOf(setting.fallbackFrom)?.fallback ??
      "#000000"
    );
  }
  return setting.fallback ?? "#000000";
}

export function Row({
  setting,
  label,
  aimed = false,
}: {
  setting: Setting;
  /** Un libellé plus court, quand la ligne est rangée sous son interrupteur. */
  label?: string;
  /** Désignée par la recherche du panneau. */
  aimed?: boolean;
}) {
  const ref = useBringIntoView<HTMLDivElement>(aimed);
  const { value, write, clear, scope, saving, catalogue, languages, detail } =
    useSettings();
  const current = value(setting.key);
  /**
   * Ce que la page dira si ce texte reste vide. Le repli du catalogue, sauf pour le nom
   * de l'événement : le site prend alors celui de la base (`event.name`, celui du BO), et
   * « Événement » seulement s'il n'y en a pas (bug #285).
   */
  const event = useEvent();
  const emptyText =
    setting.key === EVENT_NAME_KEY
      ? eventNameOf(undefined, event?.name) || setting.fallback
      : setting.fallback;
  const effectiveFlag =
    typeof current === "boolean" ? current : setting.onByDefault === true;

  /**
   * TRANSLATE — offered only where it can mean something.
   *
   * Prose only — text or an HTML block: a colour or a switch has nothing to
   * translate. The REFERENCE language only: the route fills every other language
   * from what it is given, so from any other the source would itself be a
   * translation and the reference would be among the things overwritten. And only
   * with something to translate — an empty field would have the model inventing.
   */
  const translatable =
    saving &&
    TRANSLATABLE_TYPES.has(setting.type) &&
    setting.translatable !== false &&
    scope.languageId === scope.defaultLanguageId &&
    typeof current === "string" &&
    current.trim().length > 0 &&
    // ⚠️ WITH A TYPOLOGY SELECTED, only when something is WRITTEN for it.
    // Offered on an inherited value, pressing it would have written that value
    // as a typology override in EVERY language — freezing something that was
    // only passing through, in overrides nobody asked for and nobody checks.
    (!scope.typoId || detail(setting.key).isSet);

  /* Désigne sur la page le bloc que règle cette ligne : voir `pointAt`. */
  const highlight = (on: boolean) => pointAt(on ? setting.target : undefined);

  // A row unmounted while hovered — the panel switching tabs, the card closing —
  // takes its highlight with it.
  useEffect(() => () => highlight(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={ref}
      className={aimed ? "row row--aimed" : "row"}
      data-set={setting.key}
      onMouseEnter={() => highlight(true)}
      onMouseLeave={() => highlight(false)}
    >
      <div className="row__l">
        <span>{label ?? setting.label}</span>

        {/* ⚠️ L'EXPLICATION EST DANS UNE BULLE, plus sous la ligne. Imprimées à demeure,
            les 69 aides du catalogue doublaient la hauteur du panneau — voir
            `panel/Aide.tsx`. Elle se range ICI, contre le libellé qu'elle explique, et
            avant l'interrupteur : la pastille suit le mot, elle ne se cherche pas. */}
        {setting.help && <Help text={setting.help} subject={setting.label} />}

        {setting.type === "boolean" && (
          <button
            type="button"
            className="sw"
            /* The EFFECTIVE state, not the stored one. A switch declared
               `onByDefault` shows what the page is doing while its key is still
               absent — drawn off, it would deny an option the visitor can see. */
            aria-pressed={effectiveFlag}
            aria-label={setting.label}
            onClick={() => write(setting.key, !effectiveFlag)}
          />
        )}
      </div>

      {setting.type === "languages" && (
        <div className="pick">
          {catalogue.map((l) => {
            const chosen = Array.isArray(current)
              ? (current as string[]).includes(l.key)
              : languages.some((a) => a.id === l.id);
            // The reference cannot be turned off: everything falls back to it,
            // and a site whose fallback is missing resolves to nothing at all.
            const locked = l.id === scope.defaultLanguageId;
            return (
              <button
                type="button"
                key={l.id}
                aria-pressed={chosen}
                disabled={locked}
                title={
                  locked
                    ? "Langue de référence : elle ne se retire pas."
                    : undefined
                }
                onClick={() => {
                  const base = Array.isArray(current)
                    ? (current as string[])
                    : languages.map((a) => a.short);
                  write(
                    setting.key,
                    chosen
                      ? base.filter((k) => k !== l.key)
                      : [...new Set([...base, l.key])],
                  );
                }}
              >
                {l.name}
              </button>
            );
          })}
        </div>
      )}

      {setting.type === "login-mode" && (
        <select
          value={loginModeOf(value)}
          aria-label={setting.label}
          onChange={(e) => {
            /* Un seul mode à la fois : les deux clés s'écrivent ensemble. */
            const mode = e.target.value;
            write(LOGIN_DISABLED_KEY, mode === "disabled");
            write(LOGIN_LINK_KEY, mode === "link");
            write(LOGIN_OTP_KEY, mode === "otp");
          }}
        >
          {/* Les libellés du catalogue : la note des réglages masqués les cite. */}
          {LOGIN_MODES.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      )}

      {setting.type === "language-default" && (
        <select
          value={
            typeof current === "string"
              ? current
              : (languages.find((l) => l.id === scope.defaultLanguageId)
                  ?.short ?? "")
          }
          aria-label={setting.label}
          onChange={(e) => write(setting.key, e.target.value)}
        >
          {/* Only among the languages that are ON: naming a reference the event
              does not offer leaves the cascade resolving against a row nobody
              can edit. */}
          {languages.map((l) => (
            <option key={l.id} value={l.short}>
              {languageName(l.short, l.name)}
            </option>
          ))}
        </select>
      )}

      {/* The hex in plain sight AND under the cursor: a brand book gives a code,
          and an organiser must be able to check theirs went in — a coloured
          square proves nothing about the last digit — then to type it. */}
      {setting.type === "color" && (
        <ColorField
          label={setting.label}
          value={typeof current === "string" ? current : ""}
          fallback={unsetColor(setting, value)}
          placeholder={
            setting.fallbackFrom
              ? "aucune, fond uni"
              : setting.fallback
                ? `par défaut : ${setting.fallback}`
                : "aucune"
          }
          onChange={(hex) => write(setting.key, hex)}
        />
      )}

      {setting.type === "text" &&
        (setting.multiline ? (
          <textarea
            className="ta-normal"
            rows={2}
            value={typeof current === "string" ? current : ""}
            placeholder={emptyText}
            aria-label={setting.label}
            onChange={(e) => write(setting.key, e.target.value)}
          />
        ) : (
          <input
            type="text"
            value={typeof current === "string" ? current : ""}
            /* What the page will say if this is left empty — see `emptyText`. */
            placeholder={emptyText}
            aria-label={setting.label}
            onChange={(e) => write(setting.key, e.target.value)}
          />
        ))}

      {/**
       * UN NOMBRE — et il était DÉCLARÉ SANS ÊTRE DESSINÉ, comme les listes de choix
       * l'ont été avant lui : la ligne montrait son intitulé, son aide, sa provenance,
       * et aucun contrôle. Le premier réglage à s'en servir est la longueur de
       * l'identifiant de société, qu'on ne pouvait donc pas régler.
       *
       * ⚠️ UN NOMBRE, PAS UNE CHAÎNE. `input type="number"` rend `"9"` ; enregistré
       * tel quel, le site le relirait avec un `typeof === "number"` qui échoue, et
       * retomberait sur sa valeur par défaut sans rien dire. Vide efface la clé — le
       * repli du catalogue reprend la main.
       */}
      {setting.type === "number" && (
        <input
          type="number"
          value={typeof current === "number" ? String(current) : ""}
          placeholder={setting.fallback}
          aria-label={setting.label}
          /* Même raison que dans le tunnel : focalisé, un champ numérique répond à la
             molette, et la valeur change en faisant défiler le panneau. */
          onWheel={(e) => e.currentTarget.blur()}
          onChange={(e) => {
            const raw = e.target.value.trim();
            const n = Number(raw);
            write(setting.key, raw === "" || !Number.isFinite(n) ? "" : n);
          }}
        />
      )}

      {/**
       * UNE DATE ET UNE HEURE — déclarée dans les types du produit, jamais dessinée
       * jusqu'ici, et c'est la clôture des inscriptions qui en a eu besoin la première.
       *
       * ⚠️ LA LÉGENDE FAIT PARTIE DU CONTRÔLE, elle n'est pas une aide de plus. Le
       * champ du navigateur n'affiche AUCUN fuseau : « 14:00 » s'y lit pareil à Paris et
       * à Séoul, alors que ce qui est rangé est un INSTANT. Sans la ligne dessous, deux
       * organisateurs de deux continents lisent deux heures différentes pour la même
       * clôture et croient chacun que l'autre s'est trompé — voir `funnel/cloture.ts`.
       *
       * ⚠️ VIDE EFFACE L'ÉCHÉANCE, comme le nombre au-dessus : c'est aussi ce que le
       * navigateur rend tant que la date est incomplète, donc rien ne se range avant
       * qu'elle ne le soit.
       *
       * ⚠️ UNE CLÔTURE DÉJÀ PASSÉE LE DIT, sans rien refuser : c'est un moyen légitime
       * de clore, mais une erreur d'année fermait aussi un événement en cours sans un
       * mot (bug #345).
       */}
      {setting.type === "datetime" && (
        <div className="when">
          <input
            type="datetime-local"
            value={fieldFromInstant(current)}
            aria-label={setting.label}
            onChange={(e) =>
              write(setting.key, instantFromField(e.target.value))
            }
          />
          {setting.key === CLOSED_AT_KEY && closingPassed(current) && (
            <p className="row__h row__h--warn" role="status">
              Date passée : les inscriptions sont closes dès maintenant.
            </p>
          )}
          <p className="row__h">{zoneNote(current)}</p>
        </div>
      )}

      {/* ONE CHOICE among what the EVENT holds — a form, a badge template. The
          options are built by the catalog, which is the only place that knows what
          kind of thing this setting points at. */}
      {CHOICE_TYPES.has(setting.type) &&
        (setting.options && setting.options.length > 0 ? (
          <select
            value={
              typeof current === "number" || typeof current === "string"
                ? String(current)
                : ""
            }
            aria-label={setting.label}
            onChange={(e) =>
              // Un identifiant, donc un NOMBRE : c'est ce que le produit range dans
              // ces clés, et ce que le reste du site compare. La chaîne vide, elle,
              // reste une chaîne — c'est « rien de choisi », pas l'identifiant 0.
              write(
                setting.key,
                e.target.value === "" ? "" : Number(e.target.value),
              )
            }
          >
            <option value="">—</option>
            {/**
             * ⚠️ RANGÉS PAR PROVENANCE QUAND ILS EN ONT UNE. Un sélecteur de formulaires
             * mélange ceux de l'ÉVÉNEMENT et ceux du DOMAINE : deux « Inscription » s'y
             * ressemblent à s'y méprendre, et modifier celui du domaine touche TOUS les
             * événements qui le servent. Le groupe le dit à la lecture.
             *
             * ⚠️ ET SANS GROUPE, RIEN NE CHANGE : les autres sélecteurs (un modèle de
             * badge, une langue) n'en portent pas, et un `optgroup` unique et sans titre
             * ajouterait un décalage pour rien.
             */}
            {grouper(setting.options).map(([group, choice]) =>
              group ? (
                <optgroup key={group} label={group}>
                  {choice.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
              ) : (
                choice.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))
              ),
            )}
            {/* ⚠️ UNE VALEUR HORS LISTE SE DIT. Un formulaire supprimé depuis, ou d'un
                autre genre que celui qu'on offre ici, laissait le select revenir sur
                « — » : l'organisateur lisait « rien de choisi » alors qu'une valeur
                était bien enregistrée et servie. Même parti que le sélecteur d'écran
                de la barre, qui affiche « Hors écran ». */}
            {current !== undefined &&
              current !== "" &&
              !setting.options.some((o) => o.value === String(current)) && (
                <option value={String(current)} disabled>
                  Hors liste (#{String(current)})
                </option>
              )}
          </select>
        ) : (
          <p className="row__h">
            {FORM_TYPES.has(setting.type)
              ? "Cet événement n'en propose aucun : créez-en un ci-dessous, ou dans le back office."
              : "Cet événement n'en propose aucun. Ils se créent dans le back office."}
          </p>
        ))}

      {/* CRÉER UN FORMULAIRE VIDE D'ICI, au lieu d'un aller-retour par le back office :
          il est posé sur l'événement, du genre que ce sélecteur attend, et choisi
          aussitôt — voir `NewForm`. */}
      {FORM_TYPES.has(setting.type) && (
        <NewForm
          type={setting.type === "select-forms-company" ? "C" : "U"}
          onCreated={(id) => write(setting.key, id)}
        />
      )}

      {/* UN CHOIX PARMI DES VALEURS FIXES, écrit tel quel.
          ⚠️ SA PROPRE LIGNE, PAS CELLE D'AU-DESSUS : les `select-*` voisins pointent
          vers une chose de l'événement et rangent son identifiant, donc un NOMBRE.
          Ici la valeur EST le mot (`"youtube"`), et passer par leur `Number()` aurait
          écrit `NaN` en base sans que rien ne le signale. */}
      {setting.type === "select-text" && setting.options && (
        <select
          value={
            typeof current === "string" ? current : (setting.fallback ?? "")
          }
          aria-label={setting.label}
          onChange={(e) =>
            /* Revenir sur « pas de choix » EFFACE la clé, au lieu d'écrire une
               chaîne vide : une valeur vide est une valeur, et elle masquerait
               l'héritage au lieu de le rendre. */
            e.target.value
              ? write(setting.key, e.target.value)
              : clear(setting.key)
          }
        >
          {/* ⚠️ ON PEUT NE RIEN CHOISIR. Sans cette ligne, la liste n'avait aucune
              entrée vide : une source choisie une fois ne pouvait plus être retirée,
              et un organisateur qui voulait revenir à la détection par défaut n'avait
              aucun geste pour le faire.
              ⚠️ SAUF QUAND L'ABSENCE A UN NOM. Un réglage qui déclare un `fallback`
              parmi ses options (« Place du bloc » : vide vaut « au-dessus ») n'a
              pas d'état « aucune » : la liste montre l'option qui vaut par défaut, et
              « Aucune » à côté d'elle disait deux fois la même chose. Relevé par
              William le 2026-09-16. */}
          {!setting.fallback && <option value="">Aucune</option>}
          {setting.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}

      {/* PLUSIEURS CHOIX PARMI DES VALEURS FIXES : une case par choix, comme les
          langues. Tant que rien n'est écrit, tout est coché, parce que c'est ce que la
          page propose. Le dernier coché ne se retire pas : une liste vide ne
          laisserait rien à choisir. */}
      {setting.type === "select-many" &&
        setting.options &&
        (() => {
          const all = setting.options.map((o) => o.value);
          const picked = Array.isArray(current)
            ? all.filter((v) => (current as unknown[]).map(String).includes(v))
            : all;
          /* Le nom de chaque choix coché, sous la liste : voir `labelKey`. */
          const names = setting.options.flatMap((o) => {
            const named = o.labelKey ? settingOf(o.labelKey) : undefined;
            return named && picked.includes(o.value)
              ? [{ option: o, setting: named }]
              : [];
          });
          return (
            <>
              <div className="pick">
                {setting.options.map((o) => {
                  const chosen = picked.includes(o.value);
                  const locked = chosen && picked.length === 1;
                  return (
                    <button
                      type="button"
                      key={o.value}
                      aria-pressed={chosen}
                      disabled={locked}
                      title={locked ? "Il en faut au moins un." : undefined}
                      onClick={() =>
                        write(
                          setting.key,
                          chosen
                            ? picked.filter((v) => v !== o.value)
                            : all.filter((v) => v === o.value || picked.includes(v)),
                        )
                      }
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              {names.length > 0 && (
                <div className="rowset__k">
                  {/* Le nom d'origine en texte grisé : vide, c'est lui que le site
                      garde. */}
                  {names.map(({ option, setting: named }) => (
                    <Row
                      key={named.key}
                      setting={{ ...named, fallback: option.label }}
                      label={option.label}
                    />
                  ))}
                </div>
              )}
            </>
          );
        })()}

      {/* The organiser's own HTML — laid out visually or written by hand, in a
          dialog. A plain textarea in a 380px column cannot lay anything out, and the
          editor weighs more than the whole application: it belongs behind a button,
          loaded when opened. */}
      {setting.type === "html" && (
        <HtmlField settingKey={setting.key} title={label ?? setting.label} />
      )}

      {/* Le gestionnaire des pages : il ne porte pas de valeur, il en pilote
          trente-six — voir `PagesManager`. */}
      {setting.type === "pages" && <PagesManager />}

      {/* Le parcours : son ordre et ses instances. Comme le gestionnaire des pages, il
          ne porte pas une valeur mais une LISTE — voir `StepsManager`. */}
      {setting.type === "steps" && <StepsManager />}

      {/* Même principe, autre destination : celui-ci n'écrit pas dans la configuration
          du front-office mais dans les champs du formulaire, au back office. QUEL
          formulaire est déclaré par la ligne (`formKey`) : il y en a deux depuis
          l'étape société, et un éditeur qui devine ouvrirait l'un pour l'autre. */}
      {setting.type === "form-editor" && setting.formKey && (
        <FormEditor formKey={setting.formKey} />
      )}

      {/* Le courrier du code de connexion : compte et modèles, écrits là où le back
          office les lit. Voir `ActionEmail`. */}
      {setting.type === "action-email" && setting.emailAction && (
        <ActionEmail action={setting.emailAction} />
      )}

      {/* Ni une valeur à saisir ni un interrupteur : la liste de ce qui a été demandé
          bloc par bloc, et le seul endroit d'où l'on peut en retirer une. */}
      {setting.type === "css" && <CssRules />}

      {/* Les champs du formulaire à rappeler sur le récapitulatif : une liste de cases,
          écrite sous la clé de la ligne — voir `RecapFields`. */}
      {setting.type === "recap-fields" && <RecapFields settingKey={setting.key} />}

      {setting.type === "upload" && (
        <Upload
          settingKey={setting.key}
          label={setting.label}
          safeKey={setting.safeKey}
          sidesKey={setting.sidesKey}
          maxWidth={setting.maxWidth}
        />
      )}

      {/* Une ligne d'état (email d'action) n'a pas de valeur à situer : pas de provenance. */}
      {setting.type !== "action-email" && (
        <Provenance
          settingKey={setting.key}
          translatable={translatable}
          prose={
            TRANSLATABLE_TYPES.has(setting.type) &&
            setting.translatable !== false
          }
        />
      )}
    </div>
  );
}
